import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Card,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Typography,
  Button,
  TextField,
  MenuItem,
  IconButton,
  Tooltip,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tabs,
  Tab,
  TablePagination,
} from "@mui/material";
import { Close, Add, Description } from "@mui/icons-material";
import { useSnackbar } from "notistack";
import dayjs from "dayjs";

import sailingService from "../../services/sailingService";
import PageHeader from "../../components/common/PageHeader";
import EmptyState from "../../components/common/EmptyState";
import SkeletonLoader from "../../components/common/SkeletonLoader";

// Pre-filled into the dialog for a sailing with nothing recorded yet so the
// admin only has to type an amount, not a title too. Purely a client-side
// convenience; the server stores whatever's actually saved (an empty array
// means nothing recorded, same as the HBOL fees dialog below). "Duties" is
// deliberately NOT here even though it lives in the same underlying mblFees
// list — it's entered from the HBOL Fees tab's "Duties (MBL)" field instead
// (see DUTIES_TITLE below), so this tab only ever shows/edits Shipping (and
// whatever other ad-hoc lines an admin adds).
const DEFAULT_MBL_FEE_TITLES = ["Shipping"];

// Case/whitespace-insensitive match for the one mblFees title that's
// actually owned by the HBOL Fees tab's dedicated field, not this tab's
// free-form list — filtered out of everything the MBL Fees tab shows/edits,
// but preserved (never dropped) when this tab saves the rest of the list.
const DUTIES_TITLE = "duties";
const isDutiesFee = (f) => (f.title || "").trim().toLowerCase() === DUTIES_TITLE;

// ── MBL Fees tab — every sailing in the DB, so an admin can record what
// was actually paid against each Master BOL (duties, shipping, port fees,
// plus any ad-hoc ones). ───────────────────────────────────────────────────
const MblFeesTab = () => {
  const { enqueueSnackbar } = useSnackbar();
  const [sailings, setSailings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [total, setTotal] = useState(0);

  const [feesDialogTarget, setFeesDialogTarget] = useState(null); // the sailing row
  const [feesDraft, setFeesDraft] = useState([]);
  const [savingFees, setSavingFees] = useState(false);

  const loadSailings = useCallback(() => {
    setLoading(true);
    sailingService
      .getAllSailingsForMblFees(page, rowsPerPage)
      .then(({ data }) => {
        setSailings(data.data || []);
        setTotal(data.pagination?.total || 0);
      })
      .catch(() => enqueueSnackbar("Failed to load sailings", { variant: "error" }))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, rowsPerPage]);

  useEffect(() => {
    loadSailings();
  }, [loadSailings]);

  const openFeesDialog = (sailing) => {
    setFeesDialogTarget(sailing);
    const visibleFees = (sailing.fees || []).filter((f) => !isDutiesFee(f));
    setFeesDraft(
      visibleFees.length
        ? visibleFees.map((f) => ({ ...f }))
        : DEFAULT_MBL_FEE_TITLES.map((title) => ({ title, amount: "" })),
    );
  };

  const closeFeesDialog = () => {
    setFeesDialogTarget(null);
    setFeesDraft([]);
  };

  const handleFeesDraftChange = (idx, field, value) =>
    setFeesDraft((rows) => rows.map((r, i) => (i === idx ? { ...r, [field]: value } : r)));

  const addFeesDraftRow = () => setFeesDraft((rows) => [...rows, { title: "", amount: "" }]);

  const removeFeesDraftRow = (idx) => setFeesDraft((rows) => rows.filter((_, i) => i !== idx));

  const handleSaveFees = async () => {
    const cleaned = feesDraft
      .map((f) => ({ title: (f.title || "").trim(), amount: f.amount }))
      .filter((f) => f.title && f.amount !== "" && Number.isFinite(Number(f.amount)));
    // This tab's dialog never shows the Duties line (it's edited from the
    // HBOL Fees tab's "Duties (MBL)" field instead), so it must be carried
    // over unchanged here rather than silently dropped on save.
    const dutiesFee = (feesDialogTarget.fees || []).find(isDutiesFee);
    const payload = dutiesFee ? [...cleaned, { title: dutiesFee.title, amount: dutiesFee.amount }] : cleaned;
    setSavingFees(true);
    try {
      const { data } = await sailingService.setSailingMblFees(
        feesDialogTarget.sailingId,
        payload.map((f) => ({ title: f.title, amount: Number(f.amount) })),
      );
      // Patch just this one row in place from the save response instead of
      // reloading the whole (paginated) list — avoids the visible
      // full-table refresh/skeleton flash on every save.
      const saved = data.data?.sailing;
      setSailings((rows) =>
        rows.map((r) =>
          r.sailingId === feesDialogTarget.sailingId
            ? { ...r, fees: saved?.fees || [], feesRecordedAt: saved?.feesRecordedAt || null }
            : r,
        ),
      );
      enqueueSnackbar("MBL fees saved", { variant: "success" });
      closeFeesDialog();
    } catch {
      enqueueSnackbar("Failed to save MBL fees", { variant: "error" });
    } finally {
      setSavingFees(false);
    }
  };

  return (
    <>
      <Card sx={{ p: 0 }}>
        {loading ? (
          <Box sx={{ p: 3 }}>
            <SkeletonLoader rows={6} />
          </Box>
        ) : sailings.length === 0 ? (
          <EmptyState icon="⛴️" title="No sailings yet" description="Sailings you create will show up here." />
        ) : (
          <Box sx={{ overflowX: "auto" }}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Sailing</TableCell>
                  <TableCell>MBL #</TableCell>
                  <TableCell>Departed</TableCell>
                  <TableCell>Fees</TableCell>
                  <TableCell />
                </TableRow>
              </TableHead>
              <TableBody>
                {sailings.map((s) => {
                  // Duties is excluded here — it's shown/edited from the
                  // HBOL Fees tab's "Duties (MBL)" field, not this tab, even
                  // though it's stored in the same underlying list.
                  const visibleFees = (s.fees || []).filter((f) => !isDutiesFee(f));
                  const feesTotal = visibleFees.reduce((sum, f) => sum + (f.amount || 0), 0);
                  return (
                    <TableRow key={s.sailingId}>
                      <TableCell>
                        <Typography variant="body2" fontWeight={600}>
                          {s.sailingNumber} — {s.vesselName}
                          {s.voyageNumber ? ` / ${s.voyageNumber}` : ""}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ fontFamily: "monospace", fontSize: 12 }}>{s.masterBillNumber || "—"}</TableCell>
                      <TableCell sx={{ fontSize: 12, color: "text.secondary" }}>
                        {s.departureDate ? dayjs(s.departureDate).format("MMM D, YYYY") : "—"}
                      </TableCell>
                      <TableCell sx={{ fontSize: 12 }}>
                        {visibleFees.length > 0 ? (
                          <Tooltip title={visibleFees.map((f) => `${f.title}: CI$${f.amount.toFixed(2)}`).join(", ")}>
                            <span>
                              {visibleFees.length} fee{visibleFees.length === 1 ? "" : "s"} — CI${feesTotal.toFixed(2)}
                            </span>
                          </Tooltip>
                        ) : (
                          <Typography variant="caption" color="text.disabled">
                            None recorded
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Button size="small" variant="outlined" onClick={() => openFeesDialog(s)}>
                          Add Fees
                        </Button>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </Box>
        )}
        <TablePagination
          component="div"
          count={total}
          page={page}
          onPageChange={(_, p) => setPage(p)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(+e.target.value);
            setPage(0);
          }}
          rowsPerPageOptions={[10, 20, 50]}
          sx={{ borderTop: "1px solid", borderColor: "divider" }}
        />
      </Card>

      <Dialog open={!!feesDialogTarget} onClose={closeFeesDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          MBL Fees — {feesDialogTarget?.sailingNumber}
          <Typography variant="body2" color="text.secondary">
            {feesDialogTarget?.masterBillNumber || "No MBL # on file"}
          </Typography>
        </DialogTitle>
        <DialogContent dividers>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
            {feesDraft.map((f, idx) => (
              <Box key={idx} sx={{ display: "flex", gap: 1, alignItems: "flex-start" }}>
                <TextField
                  size="small"
                  label="Title"
                  placeholder="e.g. Customs Broker Fee"
                  fullWidth
                  value={f.title}
                  onChange={(e) => handleFeesDraftChange(idx, "title", e.target.value)}
                />
                <TextField
                  size="small"
                  label="Fee (CI$)"
                  type="number"
                  sx={{ width: 160 }}
                  placeholder="0.00"
                  value={f.amount}
                  onChange={(e) => handleFeesDraftChange(idx, "amount", e.target.value)}
                />
                <Tooltip title="Remove">
                  <IconButton size="small" onClick={() => removeFeesDraftRow(idx)} sx={{ mt: 0.5 }}>
                    <Close fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Box>
            ))}
            <Button size="small" startIcon={<Add />} onClick={addFeesDraftRow} sx={{ alignSelf: "flex-start" }}>
              Add another fee
            </Button>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={closeFeesDialog}>Cancel</Button>
          <Button variant="contained" onClick={handleSaveFees} disabled={savingFees}>
            {savingFees ? <CircularProgress size={18} /> : "Save Fees"}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

// ── HBOL Fees tab — moved here from the Customs > House BOL tab, which is
// now picker-only (View/Download a House BOL — see AdminCustoms.jsx). Every
// House Bill that exists in the DB, grouped by sailing, with a fee dialog
// per HBOL and a TIN-assign dropdown for any HBOL still missing one. Each
// sailing group also carries its own "Duties (MBL)" field — the MBL-level
// half of a CBC customs payment, same underlying mblFees "Duties" line the
// MBL Fees tab edits, surfaced here too since a CBC invoice routinely
// covers the master declaration AND several house declarations together,
// so an admin can enter the whole thing without switching tabs. ───────────
const DEFAULT_HBOL_FEE_TITLES = ["Duties"];

const HbolFeesTab = () => {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const [sailingGroups, setSailingGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [total, setTotal] = useState(0);
  const [feesDialogTarget, setFeesDialogTarget] = useState(null); // { sailingId, row }
  const [feesDraft, setFeesDraft] = useState([]);
  const [savingFees, setSavingFees] = useState(false);
  const [savingKey, setSavingKey] = useState(""); // which TIN dropdown is saving, "customerId::tin"
  const [mblDutyDrafts, setMblDutyDrafts] = useState({}); // sailingId -> draft string
  const [savingMblDuty, setSavingMblDuty] = useState(""); // sailingId currently saving

  const loadHouseBols = useCallback(() => {
    setLoading(true);
    sailingService
      .getAllHouseBols(page, rowsPerPage)
      .then(({ data }) => {
        setSailingGroups(data.data || []);
        setTotal(data.pagination?.total || 0);
      })
      .catch(() => enqueueSnackbar("Failed to load House BOLs", { variant: "error" }))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, rowsPerPage]);

  useEffect(() => {
    loadHouseBols();
  }, [loadHouseBols]);

  const draftKey = (customerId, tin) => `${customerId}::${tin || ""}`;

  const openFeesDialog = (groupSailingId, row) => {
    setFeesDialogTarget({ sailingId: groupSailingId, row });
    setFeesDraft(
      row.fees?.length
        ? row.fees.map((f) => ({ ...f }))
        : DEFAULT_HBOL_FEE_TITLES.map((title) => ({ title, amount: "" })),
    );
  };

  // The sailing-level "Duties (MBL)" field's current draft value — typed
  // edits live in mblDutyDrafts until saved; falls back to whatever's
  // already recorded (sailingFeeService.setSailingMblFeeLine's "Duties"
  // line) once no local edit is in progress.
  const mblDutyValue = (group) =>
    mblDutyDrafts[group.sailingId] ?? (group.mblDuty != null ? String(group.mblDuty) : "");

  const handleSaveMblDuty = async (group) => {
    const amount = mblDutyValue(group);
    setSavingMblDuty(group.sailingId);
    try {
      const { data } = await sailingService.setSailingMblFeeLine(group.sailingId, "Duties", amount);
      const saved = data.data?.sailing;
      const newAmount = saved?.fees?.find((f) => f.title === "Duties")?.amount ?? null;
      setSailingGroups((groups) =>
        groups.map((g) => (g.sailingId === group.sailingId ? { ...g, mblDuty: newAmount } : g)),
      );
      setMblDutyDrafts((drafts) => {
        const next = { ...drafts };
        delete next[group.sailingId];
        return next;
      });
      enqueueSnackbar("MBL Duty saved", { variant: "success" });
    } catch {
      enqueueSnackbar("Failed to save MBL Duty", { variant: "error" });
    } finally {
      setSavingMblDuty("");
    }
  };

  const closeFeesDialog = () => {
    setFeesDialogTarget(null);
    setFeesDraft([]);
  };

  const handleFeesDraftChange = (idx, field, value) =>
    setFeesDraft((rows) => rows.map((r, i) => (i === idx ? { ...r, [field]: value } : r)));

  const addFeesDraftRow = () => setFeesDraft((rows) => [...rows, { title: "", amount: "" }]);

  const removeFeesDraftRow = (idx) => setFeesDraft((rows) => rows.filter((_, i) => i !== idx));

  const handleSaveFees = async () => {
    const { sailingId: targetSailingId, row } = feesDialogTarget;
    const cleaned = feesDraft
      .map((f) => ({ title: (f.title || "").trim(), amount: f.amount }))
      .filter((f) => f.title && f.amount !== "" && Number.isFinite(Number(f.amount)));
    setSavingFees(true);
    try {
      const { data } = await sailingService.setHouseBolFees(
        targetSailingId,
        row.customerId,
        row.tin,
        cleaned.map((f) => ({ title: f.title, amount: Number(f.amount) })),
      );
      // Patch just this one HBOL row in place from the save response
      // instead of reloading the whole (paginated) list — avoids the
      // visible full-table refresh/skeleton flash on every save.
      const saved = data.data?.houseBol;
      setSailingGroups((groups) =>
        groups.map((g) =>
          g.sailingId !== targetSailingId
            ? g
            : {
              ...g,
              houseBols: g.houseBols.map((r) =>
                r.customerId === row.customerId && r.tin === row.tin
                  ? { ...r, fees: saved?.fees || [], feesRecordedAt: saved?.feesRecordedAt || null }
                  : r,
              ),
            },
        ),
      );
      enqueueSnackbar("Fees saved", { variant: "success" });
      closeFeesDialog();
    } catch {
      enqueueSnackbar("Failed to save fees", { variant: "error" });
    } finally {
      setSavingFees(false);
    }
  };

  const handleAssignTin = async (groupSailingId, row, newTin) => {
    const key = draftKey(row.customerId, row.tin);
    setSavingKey(key);
    try {
      await sailingService.setHouseBolTin(groupSailingId, row.customerId, newTin);
      enqueueSnackbar("TIN assigned", { variant: "success" });
      loadHouseBols();
    } catch {
      enqueueSnackbar("Failed to assign TIN", { variant: "error" });
    } finally {
      setSavingKey("");
    }
  };

  return (
    <>
      <Card sx={{ p: 0 }}>
        {loading ? (
          <Box sx={{ p: 3 }}>
            <SkeletonLoader rows={6} />
          </Box>
        ) : sailingGroups.length === 0 ? (
          <Box sx={{ p: 3 }}>
            <Typography variant="body2" color="text.secondary">
              No House Bills have been generated yet — use the picker on the Customs &gt; House BOL tab to view or
              download one.
            </Typography>
          </Box>
        ) : (
          sailingGroups.map((group) => (
            <Box key={group.sailingId} sx={{ borderTop: "1px solid", borderColor: "divider" }}>
              <Box
                sx={{
                  px: 2.5,
                  py: 1.5,
                  bgcolor: "action.hover",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  flexWrap: "wrap",
                  gap: 2,
                }}
              >
                <Box>
                  <Typography variant="body2" fontWeight={700}>
                    {group.sailingNumber} — {group.vesselName}
                    {group.voyageNumber ? ` / ${group.voyageNumber}` : ""}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" display="block">
                    <strong>MBL#: </strong> {group.masterBillNumber || "—"}
                  </Typography>
                  <br />
                  <Typography variant="caption" color="text.secondary" display="block">
                    Departed {dayjs(group.departureDate).format("MMM D, YYYY")}
                  </Typography>
                </Box>

                {/* MBL-level half of a CBC customs payment — see the
                    DEFAULT_HBOL_FEE_TITLES comment above this component. */}
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <TextField
                    size="small"
                    label="Duties (MBL)"
                    type="number"
                    placeholder="0.00"
                    value={mblDutyValue(group)}
                    onChange={(e) =>
                      setMblDutyDrafts((drafts) => ({ ...drafts, [group.sailingId]: e.target.value }))
                    }
                    sx={{ width: 150, bgcolor: "background.paper" }}
                  />
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={() => handleSaveMblDuty(group)}
                    disabled={savingMblDuty === group.sailingId}
                  >
                    {savingMblDuty === group.sailingId ? <CircularProgress size={16} /> : "Save"}
                  </Button>
                </Box>
              </Box>
              <Box sx={{ overflowX: "auto" }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Customer</TableCell>
                      <TableCell>TIN</TableCell>
                      <TableCell>HBL #</TableCell>
                      <TableCell>Fees</TableCell>
                      <TableCell />
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {group.houseBols.map((row) => {
                      const key = draftKey(row.customerId, row.tin);
                      const feesTotal = (row.fees || []).reduce((sum, f) => sum + (f.amount || 0), 0);
                      return (
                        <TableRow key={key}>
                          <TableCell>
                            <Typography variant="body2" fontWeight={600}>
                              {row.customerName}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {row.customerEmail}
                            </Typography>
                          </TableCell>
                          <TableCell sx={{ fontFamily: "monospace", fontSize: 12, minWidth: 160 }}>
                            {row.tin ? (
                              row.tin
                            ) : row.availableTins?.length > 0 ? (
                              <TextField
                                select
                                size="small"
                                fullWidth
                                label="Pick TIN"
                                value=""
                                disabled={savingKey === key}
                                onChange={(e) => handleAssignTin(group.sailingId, row, e.target.value)}
                              >
                                {row.availableTins.map((t) => (
                                  <MenuItem key={t.tin} value={t.tin}>
                                    {t.label ? `${t.tin} (${t.label})` : t.tin}
                                  </MenuItem>
                                ))}
                              </TextField>
                            ) : (
                              <Tooltip title="This customer has no TIN on file to pick from">
                                <span>—</span>
                              </Tooltip>
                            )}
                          </TableCell>
                          <TableCell sx={{ fontFamily: "monospace", fontSize: 12 }}>{row.hblNumber}</TableCell>
                          <TableCell sx={{ fontSize: 12 }}>
                            {row.fees?.length > 0 ? (
                              <Tooltip title={row.fees.map((f) => `${f.title}: CI$${f.amount.toFixed(2)}`).join(", ")}>
                                <span>
                                  {row.fees.length} fee{row.fees.length === 1 ? "" : "s"} — CI${feesTotal.toFixed(2)}
                                </span>
                              </Tooltip>
                            ) : (
                              <Typography variant="caption" color="text.disabled">
                                None recorded
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell>
                            <Box sx={{ display: "flex", gap: 0.5 }}>
                              <Button size="small" variant="outlined" onClick={() => openFeesDialog(group.sailingId, row)}>
                                Add Fees
                              </Button>
                              <Button
                                size="small"
                                variant="outlined"
                                startIcon={<Description sx={{ fontSize: 14 }} />}
                                onClick={() =>
                                  navigate(
                                    `/admin/sailing/${group.sailingId}/house-bol/${row.customerId}${row.tin ? `?tin=${encodeURIComponent(row.tin)}` : ""}`,
                                  )
                                }
                              >
                                View HBOL
                              </Button>
                            </Box>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </Box>
              {/* Sailing-wide total: every HBOL's fees on this sailing plus
                  the sailing-level Duties (MBL) field above — the same
                  "Duties" line the MBL Fees tab also stores, but shown once
                  here since this is where it's actually entered. */}
              <Box sx={{ px: 2.5, py: 1, display: "flex", justifyContent: "flex-end", borderTop: "1px solid", borderColor: "divider" }}>
                <Typography variant="body2" fontWeight={700}>
                  Sailing Total: CI$
                  {(
                    group.houseBols.reduce(
                      (sum, row) => sum + (row.fees || []).reduce((s, f) => s + (f.amount || 0), 0),
                      0,
                    ) + (Number(group.mblDuty) || 0)
                  ).toFixed(2)}
                </Typography>
              </Box>
            </Box>
          ))
        )}
        <TablePagination
          component="div"
          count={total}
          page={page}
          onPageChange={(_, p) => setPage(p)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(+e.target.value);
            setPage(0);
          }}
          rowsPerPageOptions={[10, 20, 50]}
          sx={{ borderTop: "1px solid", borderColor: "divider" }}
        />
      </Card>

      <Dialog open={!!feesDialogTarget} onClose={closeFeesDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          Fees for {feesDialogTarget?.row.hblNumber}
          <Typography variant="body2" color="text.secondary">
            {feesDialogTarget?.row.customerName}
          </Typography>
        </DialogTitle>
        <DialogContent dividers>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
            {feesDraft.map((f, idx) => (
              <Box key={idx} sx={{ display: "flex", gap: 1, alignItems: "flex-start" }}>
                <TextField
                  size="small"
                  label="Title"
                  placeholder="e.g. Courier fee"
                  fullWidth
                  value={f.title}
                  onChange={(e) => handleFeesDraftChange(idx, "title", e.target.value)}
                />
                <TextField
                  size="small"
                  label="Fee (CI$)"
                  type="number"
                  sx={{ width: 160 }}
                  placeholder="0.00"
                  value={f.amount}
                  onChange={(e) => handleFeesDraftChange(idx, "amount", e.target.value)}
                />
                <Tooltip title="Remove">
                  <IconButton size="small" onClick={() => removeFeesDraftRow(idx)} sx={{ mt: 0.5 }}>
                    <Close fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Box>
            ))}
            <Button size="small" startIcon={<Add />} onClick={addFeesDraftRow} sx={{ alignSelf: "flex-start" }}>
              Add another fee
            </Button>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={closeFeesDialog}>Cancel</Button>
          <Button variant="contained" onClick={handleSaveFees} disabled={savingFees}>
            {savingFees ? <CircularProgress size={18} /> : "Save Fees"}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

// ── Port Fees tab — the Port Authority bills per Dock Receipt (a Cargo
// Charge + a Trucking Charge each), not per sailing or per customer, so
// this lists every D/R on a sailing's roster (derived from the real items,
// same as the Manifest/weight-CBF split — not admin-typed) grouped by
// sailing, same layout as HBOL Fees above. A D/R can span items from more
// than one shipment/customer, which is exactly why this is its own list
// rather than folded into MBL or HBOL fees. ─────────────────────────────────
const PortFeesTab = () => {
  const { enqueueSnackbar } = useSnackbar();
  const [sailingGroups, setSailingGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  // Deriving the D/R roster per sailing means joining across every shipment
  // on it (see sailingFeeService.listAllSailingsForPortFees) — much
  // heavier per sailing than MBL/HBOL Fees' plain field reads, so this tab
  // defaults to a much smaller page size to keep each load fast.
  const [rowsPerPage, setRowsPerPage] = useState(3);
  const [total, setTotal] = useState(0);
  const [feesDialogTarget, setFeesDialogTarget] = useState(null); // { sailingId, dr }
  const [feesDraft, setFeesDraft] = useState({ cargoCharge: "", truckingCharge: "", invoiceNumber: "", invoiceDate: "" });
  const [savingFees, setSavingFees] = useState(false);

  // Ad-hoc, sailing-wide Port Fees (not tied to one D/R — e.g. a wharfage
  // surcharge billed once for the whole sailing) — same "titled line items,
  // edit the whole list at once" dialog pattern as MBL Fees.
  const [extraFeesDialogTarget, setExtraFeesDialogTarget] = useState(null); // sailingId
  const [extraFeesDraft, setExtraFeesDraft] = useState([]);
  const [savingExtraFees, setSavingExtraFees] = useState(false);

  const loadSailings = useCallback(() => {
    setLoading(true);
    sailingService
      .getAllSailingsForPortFees(page, rowsPerPage)
      .then(({ data }) => {
        setSailingGroups(data.data || []);
        setTotal(data.pagination?.total || 0);
      })
      .catch(() => enqueueSnackbar("Failed to load sailings", { variant: "error" }))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, rowsPerPage]);

  useEffect(() => {
    loadSailings();
  }, [loadSailings]);

  const openFeesDialog = (groupSailingId, dr) => {
    setFeesDialogTarget({ sailingId: groupSailingId, dr });
    setFeesDraft({
      cargoCharge: dr.cargoCharge || "",
      truckingCharge: dr.truckingCharge || "",
      invoiceNumber: dr.invoiceNumber || "",
      invoiceDate: dr.invoiceDate ? dayjs(dr.invoiceDate).format("YYYY-MM-DD") : "",
    });
  };

  const closeFeesDialog = () => {
    setFeesDialogTarget(null);
    setFeesDraft({ cargoCharge: "", truckingCharge: "", invoiceNumber: "", invoiceDate: "" });
  };

  const handleSaveFees = async () => {
    const { sailingId: targetSailingId, dr } = feesDialogTarget;
    const group = sailingGroups.find((g) => g.sailingId === targetSailingId);

    // Replaces the WHOLE list for this sailing (server stores it as one
    // set, same as MBL/HBOL fees) — so every other D/R on this sailing is
    // carried over unchanged, only this one D/R's row is updated/added.
    const nextFees = (group?.dockReceipts || [])
      .map((d) =>
        d.dockReceiptNumber === dr.dockReceiptNumber
          ? {
            dockReceiptNumber: d.dockReceiptNumber,
            cargoCharge: feesDraft.cargoCharge,
            truckingCharge: feesDraft.truckingCharge,
            invoiceNumber: feesDraft.invoiceNumber,
            invoiceDate: feesDraft.invoiceDate,
          }
          : {
            dockReceiptNumber: d.dockReceiptNumber,
            cargoCharge: d.cargoCharge,
            truckingCharge: d.truckingCharge,
            invoiceNumber: d.invoiceNumber,
            invoiceDate: d.invoiceDate,
          },
      )
      .filter((f) => Number(f.cargoCharge) > 0 || Number(f.truckingCharge) > 0);

    setSavingFees(true);
    try {
      const { data } = await sailingService.setSailingPortFees(
        targetSailingId,
        nextFees.map((f) => ({
          dockReceiptNumber: f.dockReceiptNumber,
          cargoCharge: Number(f.cargoCharge) || 0,
          truckingCharge: Number(f.truckingCharge) || 0,
          invoiceNumber: f.invoiceNumber,
          invoiceDate: f.invoiceDate || undefined,
        })),
      );
      // Patch just this sailing's D/R rows in place from the save response
      // (the canonical, now-current fees list) instead of reloading the
      // whole (paginated) list — avoids the visible full-table
      // refresh/skeleton flash on every save. `pieces` isn't part of the
      // save response (it's derived from the live roster, not stored), so
      // it's carried over unchanged from what's already in state.
      const savedByDr = new Map((data.data?.sailing?.fees || []).map((f) => [f.dockReceiptNumber, f]));
      setSailingGroups((groups) =>
        groups.map((g) =>
          g.sailingId !== targetSailingId
            ? g
            : {
              ...g,
              dockReceipts: g.dockReceipts.map((d) => {
                const fee = savedByDr.get(d.dockReceiptNumber);
                return {
                  ...d,
                  cargoCharge: fee?.cargoCharge || 0,
                  truckingCharge: fee?.truckingCharge || 0,
                  invoiceNumber: fee?.invoiceNumber || "",
                  invoiceDate: fee?.invoiceDate || null,
                  hasFee: !!fee,
                };
              }),
            },
        ),
      );
      enqueueSnackbar("Port fee saved", { variant: "success" });
      closeFeesDialog();
    } catch {
      enqueueSnackbar("Failed to save port fee", { variant: "error" });
    } finally {
      setSavingFees(false);
    }
  };

  const openExtraFeesDialog = (group) => {
    setExtraFeesDialogTarget(group.sailingId);
    setExtraFeesDraft(group.extraFees?.length ? group.extraFees.map((f) => ({ ...f })) : [{ title: "", amount: "" }]);
  };

  const closeExtraFeesDialog = () => {
    setExtraFeesDialogTarget(null);
    setExtraFeesDraft([]);
  };

  const handleExtraFeesDraftChange = (idx, field, value) =>
    setExtraFeesDraft((rows) => rows.map((r, i) => (i === idx ? { ...r, [field]: value } : r)));

  const addExtraFeesDraftRow = () => setExtraFeesDraft((rows) => [...rows, { title: "", amount: "" }]);

  const removeExtraFeesDraftRow = (idx) => setExtraFeesDraft((rows) => rows.filter((_, i) => i !== idx));

  const handleSaveExtraFees = async () => {
    const cleaned = extraFeesDraft
      .map((f) => ({ title: (f.title || "").trim(), amount: f.amount }))
      .filter((f) => f.title && f.amount !== "" && Number.isFinite(Number(f.amount)));
    setSavingExtraFees(true);
    try {
      const { data } = await sailingService.setSailingPortFeesExtra(
        extraFeesDialogTarget,
        cleaned.map((f) => ({ title: f.title, amount: Number(f.amount) })),
      );
      const saved = data.data?.sailing;
      setSailingGroups((groups) =>
        groups.map((g) =>
          g.sailingId !== extraFeesDialogTarget
            ? g
            : { ...g, extraFees: saved?.fees || [], extraFeesRecordedAt: saved?.feesRecordedAt || null },
        ),
      );
      enqueueSnackbar("Port fees saved", { variant: "success" });
      closeExtraFeesDialog();
    } catch {
      enqueueSnackbar("Failed to save port fees", { variant: "error" });
    } finally {
      setSavingExtraFees(false);
    }
  };

  return (
    <>
      <Card sx={{ p: 0 }}>
        {loading ? (
          <Box sx={{ p: 3 }}>
            <SkeletonLoader rows={6} />
          </Box>
        ) : sailingGroups.length === 0 ? (
          <EmptyState icon="⚓" title="No Dock Receipts yet" description="D/Rs assigned to items will show up here." />
        ) : (
          sailingGroups.map((group) => (
            <Box key={group.sailingId} sx={{ borderTop: "1px solid", borderColor: "divider" }}>
              <Box sx={{ px: 2.5, py: 1.5, bgcolor: "action.hover" }}>
                <Typography variant="body2" fontWeight={700}>
                  {group.sailingNumber} — {group.vesselName}
                  {group.voyageNumber ? ` / ${group.voyageNumber}` : ""}
                </Typography>
                <Typography variant="caption" color="text.secondary" display="block">
                  <strong>MBL#: </strong> {group.masterBillNumber || "—"}
                </Typography>
                <br />
                <Typography variant="caption" color="text.secondary" display="block">
                  Departed {dayjs(group.departureDate).format("MMM D, YYYY")}
                </Typography>
              </Box>
              <Box sx={{ overflowX: "auto" }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>D/R #</TableCell>
                      <TableCell align="center">Pieces</TableCell>
                      <TableCell>Invoice #</TableCell>
                      <TableCell align="right">Cargo Charge</TableCell>
                      <TableCell align="right">Trucking Charge</TableCell>
                      <TableCell align="right">Total</TableCell>
                      <TableCell />
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {group.dockReceipts.map((dr) => (
                      <TableRow key={dr.dockReceiptNumber}>
                        <TableCell sx={{ fontFamily: "monospace", fontSize: 12 }}>{dr.dockReceiptNumber}</TableCell>
                        <TableCell align="center">{dr.pieces}</TableCell>
                        <TableCell sx={{ fontFamily: "monospace", fontSize: 12 }}>{dr.invoiceNumber || "—"}</TableCell>
                        <TableCell align="right" sx={{ fontSize: 12 }}>
                          {dr.hasFee ? `CI$${dr.cargoCharge.toFixed(2)}` : <Typography variant="caption" color="text.disabled">—</Typography>}
                        </TableCell>
                        <TableCell align="right" sx={{ fontSize: 12 }}>
                          {dr.hasFee ? `CI$${dr.truckingCharge.toFixed(2)}` : <Typography variant="caption" color="text.disabled">—</Typography>}
                        </TableCell>
                        <TableCell align="right" sx={{ fontSize: 12, fontWeight: 700 }}>
                          {dr.hasFee ? (
                            `CI$${(dr.cargoCharge + dr.truckingCharge).toFixed(2)}`
                          ) : (
                            <Typography variant="caption" color="text.disabled">
                              —
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          <Button size="small" variant="outlined" onClick={() => openFeesDialog(group.sailingId, dr)}>
                            {dr.hasFee ? "Edit Fee" : "Add Fee"}
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Box>
              {/* Ad-hoc, sailing-wide Port Fees — not tied to any one D/R
                  (e.g. a wharfage surcharge billed once for the sailing),
                  same "Add Fee" pattern MBL Fees uses. */}
              <Box
                sx={{
                  px: 2.5,
                  py: 1,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  borderTop: "1px solid",
                  borderColor: "divider",
                  flexWrap: "wrap",
                  gap: 1,
                }}
              >
                <Typography variant="body2">
                  <strong>Other Port Fees:</strong>{" "}
                  {group.extraFees?.length > 0 ? (
                    <Tooltip title={group.extraFees.map((f) => `${f.title}: CI$${f.amount.toFixed(2)}`).join(", ")}>
                      <span>
                        {group.extraFees.length} fee{group.extraFees.length === 1 ? "" : "s"} — CI$
                        {group.extraFees.reduce((sum, f) => sum + (f.amount || 0), 0).toFixed(2)}
                      </span>
                    </Tooltip>
                  ) : (
                    <Typography component="span" variant="caption" color="text.disabled">
                      None recorded
                    </Typography>
                  )}
                </Typography>
                <Button size="small" variant="outlined" onClick={() => openExtraFeesDialog(group)}>
                  {group.extraFees?.length > 0 ? "Edit Fees" : "Add Fee"}
                </Button>
              </Box>
              {/* Sailing-wide total across every D/R's Cargo + Trucking charge
                  PLUS the ad-hoc fees above — D/Rs with no fee entered yet
                  don't count toward it, same as the per-row Total column. */}
              <Box sx={{ px: 2.5, py: 1, display: "flex", justifyContent: "flex-end", borderTop: "1px solid", borderColor: "divider" }}>
                <Typography variant="body2" fontWeight={700}>
                  Sailing Total: CI$
                  {(
                    group.dockReceipts
                      .filter((dr) => dr.hasFee)
                      .reduce((sum, dr) => sum + dr.cargoCharge + dr.truckingCharge, 0) +
                    (group.extraFees || []).reduce((sum, f) => sum + (f.amount || 0), 0)
                  ).toFixed(2)}
                </Typography>
              </Box>
            </Box>
          ))
        )}
        <TablePagination
          component="div"
          count={total}
          page={page}
          onPageChange={(_, p) => setPage(p)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(+e.target.value);
            setPage(0);
          }}
          rowsPerPageOptions={[3, 5, 10]}
          sx={{ borderTop: "1px solid", borderColor: "divider" }}
        />
      </Card>

      <Dialog open={!!feesDialogTarget} onClose={closeFeesDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          Port Fee — D/R {feesDialogTarget?.dr.dockReceiptNumber}
          <Typography variant="body2" color="text.secondary">
            {feesDialogTarget?.dr.pieces} piece{feesDialogTarget?.dr.pieces === 1 ? "" : "s"}
          </Typography>
        </DialogTitle>
        <DialogContent dividers>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
            <Box sx={{ display: "flex", gap: 1.5 }}>
              <TextField
                size="small"
                label="Cargo Charge (CI$)"
                type="number"
                fullWidth
                placeholder="0.00"
                value={feesDraft.cargoCharge}
                onChange={(e) => setFeesDraft((d) => ({ ...d, cargoCharge: e.target.value }))}
              />
              <TextField
                size="small"
                label="Trucking Charge (CI$)"
                type="number"
                fullWidth
                placeholder="0.00"
                value={feesDraft.truckingCharge}
                onChange={(e) => setFeesDraft((d) => ({ ...d, truckingCharge: e.target.value }))}
              />
            </Box>
            <Box sx={{ display: "flex", gap: 1.5 }}>
              <TextField
                size="small"
                label="Invoice # (optional)"
                fullWidth
                value={feesDraft.invoiceNumber}
                onChange={(e) => setFeesDraft((d) => ({ ...d, invoiceNumber: e.target.value }))}
              />
              <TextField
                size="small"
                label="Invoice Date"
                type="date"
                fullWidth
                value={feesDraft.invoiceDate}
                onChange={(e) => setFeesDraft((d) => ({ ...d, invoiceDate: e.target.value }))}
                slotProps={{ inputLabel: { shrink: true } }}
              />
            </Box>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={closeFeesDialog}>Cancel</Button>
          <Button variant="contained" onClick={handleSaveFees} disabled={savingFees}>
            {savingFees ? <CircularProgress size={18} /> : "Save Fee"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!extraFeesDialogTarget} onClose={closeExtraFeesDialog} maxWidth="sm" fullWidth>
        <DialogTitle>Other Port Fees</DialogTitle>
        <DialogContent dividers>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
            {extraFeesDraft.map((f, idx) => (
              <Box key={idx} sx={{ display: "flex", gap: 1, alignItems: "flex-start" }}>
                <TextField
                  size="small"
                  label="Title"
                  placeholder="e.g. Wharfage Surcharge"
                  fullWidth
                  value={f.title}
                  onChange={(e) => handleExtraFeesDraftChange(idx, "title", e.target.value)}
                />
                <TextField
                  size="small"
                  label="Fee (CI$)"
                  type="number"
                  sx={{ width: 160 }}
                  placeholder="0.00"
                  value={f.amount}
                  onChange={(e) => handleExtraFeesDraftChange(idx, "amount", e.target.value)}
                />
                <Tooltip title="Remove">
                  <IconButton size="small" onClick={() => removeExtraFeesDraftRow(idx)} sx={{ mt: 0.5 }}>
                    <Close fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Box>
            ))}
            <Button size="small" startIcon={<Add />} onClick={addExtraFeesDraftRow} sx={{ alignSelf: "flex-start" }}>
              Add another fee
            </Button>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={closeExtraFeesDialog}>Cancel</Button>
          <Button variant="contained" onClick={handleSaveExtraFees} disabled={savingExtraFees}>
            {savingExtraFees ? <CircularProgress size={18} /> : "Save Fees"}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

const AdminExpenses = () => {
  const [tab, setTab] = useState(0);

  return (
    <Box>
      <PageHeader title="Expenses" subtitle="Record fees paid against each sailing's MBL and each customer's HBOL" />

      <Tabs
        value={tab}
        onChange={(_, v) => setTab(v)}
        sx={{
          mb: 2,
          "& .MuiTab-root": { textTransform: "none", fontWeight: 600, fontSize: 13, minHeight: 40 },
        }}
      >
        <Tab label="MBL Fees" />
        <Tab label="HBOL Fees" />
        <Tab label="Port Fees" />
      </Tabs>

      {tab === 0 && <MblFeesTab />}
      {tab === 1 && <HbolFeesTab />}
      {tab === 2 && <PortFeesTab />}
    </Box>
  );
};

export default AdminExpenses;
